// ============================================================================
// Payment Link Service
// Resend / renew payment-link emails via the existing hcp-admin-backend
// proxies (which forward to hcp-backend). The token and URL never reach the
// dashboard — the email URL is built server-side from hcp-backend's
// PAYMENT_LINK_BASE_URL — so fixing a misconfigured URL means correcting
// that env var and then resending from here.
// See Documents/202607-PAYMENT_LINK_FIX_GUIDE.md.
// ============================================================================

import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

export interface PaymentLinkSendResult {
  success: boolean;
  confirmation_code: string;
  sent_to: string;
  amount: number;
  expires_at: string;
  message: string;
}

export type PaymentLinkErrorKind = 'expired' | 'consumed' | 'not_found' | 'other';

export class PaymentLinkError extends Error {
  kind: PaymentLinkErrorKind;

  constructor(kind: PaymentLinkErrorKind, message: string) {
    super(message);
    this.kind = kind;
  }
}

/**
 * hcp-backend signals "expired" with a structured 410 detail object, which
 * the shared fetch wrapper stringifies to "[object Object]". Classify by
 * message content so the UI can offer the right next action.
 */
function classifyError(err: unknown): PaymentLinkError {
  const message = err instanceof Error ? err.message : String(err);
  if (message === '[object Object]' || /expired/i.test(message)) {
    return new PaymentLinkError(
      'expired',
      'This payment link has expired — renew it to email the guest a fresh link.'
    );
  }
  if (/already been used|payment was completed/i.test(message)) {
    return new PaymentLinkError('consumed', 'This payment link was already used — the payment is complete.');
  }
  if (/no payment link found/i.test(message)) {
    return new PaymentLinkError('not_found', 'No payment link exists for this reservation.');
  }
  return new PaymentLinkError('other', message || 'Payment link request failed');
}

/** Resend the existing (active) payment link email. 410-expired → renew. */
export async function resendPaymentLink(
  confirmationCode: string,
  overrideEmail?: string
): Promise<PaymentLinkSendResult> {
  try {
    return await fetchWithAuth(`${API_BASE_URL}/reservations/resend-payment-link`, {
      method: 'POST',
      body: JSON.stringify({
        confirmation_code: confirmationCode,
        override_email: overrideEmail || undefined,
      }),
    });
  } catch (err) {
    throw classifyError(err);
  }
}

/** Issue a fresh token (for expired links) and email it. */
export async function renewPaymentLink(
  confirmationCode: string,
  overrideEmail?: string
): Promise<PaymentLinkSendResult> {
  try {
    return await fetchWithAuth(`${API_BASE_URL}/reservations/renew-payment-link`, {
      method: 'POST',
      body: JSON.stringify({
        confirmation_code: confirmationCode,
        override_email: overrideEmail || undefined,
      }),
    });
  } catch (err) {
    throw classifyError(err);
  }
}
